import { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Eye,
  DocumentDownload,
  MagicStar,
  Element4,
  ShoppingBag,
  Timer,
  Gallery,
  Verify,
  PlayCircle,
} from 'iconsax-react';
import TitleText from '../../atoms/TitleText';
import InfoText from '../../atoms/InfoText';
import Button from '../../atoms/Button';
import CustomModal from '../../atoms/CustomModal';
import Map from '../../atoms/Map';
import { Image, Skeleton } from 'antd';
import {
  useGetPostOwnerQuery,
  useGetPostInfoQuery,
  useGetPostLocationsQuery,
  useGetPostAttachmentsQuery,
  useGetPostPaymentInfoQuery,
} from '../../../store/services/biddingPostsService';

interface BiddingPostInformationTabProps {
  postId: string;
}

const formatFileSize = (bytes?: number) => {
  if (!bytes) return '-';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

export default function BiddingPostInformationTab({ postId }: BiddingPostInformationTabProps) {
  const { t } = useTranslation();

  const { data: ownerData, isLoading: isOwnerLoading } = useGetPostOwnerQuery(
    { postId },
    { skip: !postId },
  );
  const { data: infoData, isLoading: isInfoLoading } = useGetPostInfoQuery(
    { postId },
    { skip: !postId },
  );
  const { data: locationsData, isLoading: isLocationsLoading } = useGetPostLocationsQuery(
    { postId },
    { skip: !postId },
  );
  const { data: attachmentsData, isLoading: isAttachmentsLoading } = useGetPostAttachmentsQuery(
    { postId },
    { skip: !postId },
  );
  const { data: paymentData, isLoading: isPaymentLoading } = useGetPostPaymentInfoQuery(
    { postId },
    { skip: !postId },
  );

  const owner = ownerData?.payload;
  const info = infoData?.payload;
  const locations = locationsData?.payload ?? [];
  const primaryLocation = locations[0];
  const attachments = attachmentsData?.payload;
  const payment = paymentData?.payload;

  const images = (attachments?.images || []).filter((img) => img.id && img.blobFileUrl);
  const videos = (attachments?.videos || []).filter((vid) => vid.id && vid.blobFileUrl);
  const media = [
    ...images.map((img) => ({ ...img, mediaType: 'image' as const })),
    ...videos.map((vid) => ({ ...vid, mediaType: 'video' as const })),
  ];

  const otherDocs = attachments?.otherDocs || [];
  const evalDocs = attachments?.evaluationDocuments || [];
  const performEvaluation = evalDocs.length > 0;

  const [avatarError, setAvatarError] = useState(false);
  const [activeVideo, setActiveVideo] = useState<{ url: string; name?: string } | null>(null);
  const [activePreviewDoc, setActivePreviewDoc] = useState<{ url: string; name?: string; isImage: boolean } | null>(null);

  const handleDownloadDoc = (url?: string, fileName?: string) => {
    if (!url) return;
    const a = document.createElement('a');
    a.href = url;
    a.download = fileName || 'document';
    a.target = '_blank';
    a.rel = 'noopener noreferrer';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  const handleViewDoc = (url?: string, fileName?: string) => {
    if (!url) return;
    const ext = (fileName || url).split('?')[0].split('.').pop()?.toLowerCase() || '';
    const isImage = ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp', 'svg'].includes(ext);
    setActivePreviewDoc({ url, name: fileName, isImage });
  };

  const numLat = primaryLocation?.latitude != null ? Number(primaryLocation.latitude) : NaN;
  const numLng = primaryLocation?.longitude != null ? Number(primaryLocation.longitude) : NaN;
  const hasValidCoords =
    !isNaN(numLat) &&
    !isNaN(numLng) &&
    (numLat !== 0 || numLng !== 0);

  const isMilestones =
    payment?.paymentMethod?.code === 'MILESTONES' ||
    Boolean(payment?.milestones && payment.milestones.length > 0);

  const quantityUnit = payment?.quantityUnit || t('biddingPosts.units');

  const ratingDisplay = useMemo(() => {
    const rate = owner?.rating;
    const reviews = owner?.reviewsCount;
    const numRate = Number(rate ?? 0);
    const numReviews = Number(reviews ?? 0);
    if (!rate || (numRate === 0 && numReviews === 0)) {
      return t('biddingPosts.zeroReviews');
    }
    const reviewsText = reviews !== undefined && reviews !== null ? ` (${reviews} ${t('biddingPosts.reviews')})` : '';
    return `${rate}${reviewsText}`;
  }, [owner?.rating, owner?.reviewsCount, t]);

  const ordersDisplay = useMemo(() => {
    const orders = owner?.ordersCount;
    const num = Number(orders ?? 0);
    if (!orders || num === 0) {
      return t('biddingPosts.zeroOrder');
    }
    return `${num}+ ${t('biddingPosts.orders')}`;
  }, [owner?.ordersCount, t]);

  const avgResponseDisplay = useMemo(() => {
    const avgResponse = owner?.averageResponseHours;
    if (!avgResponse) return null;
    const str = String(avgResponse).trim();
    const timeStr = /^\d+$/.test(str) ? `${str}hrs` : str.replace(/\s*response$/i, '');
    return t('biddingPosts.avgResponse', { time: timeStr });
  }, [owner?.averageResponseHours, t]);

  return (
    <div className="grid grid-cols-1 lg:grid-cols-12 gap-6" data-testid="bidding-post-info-tab-container">
      {/* ── Left Column (~33% / 4 cols) ─────────────────────────────────── */}
      <div className="lg:col-span-4 space-y-6" data-testid="bidding-post-info-left-column">
        {/* 1. About the Buyer */}
        <div
          className="bg-white dark:bg-dark-surface-primary rounded-[16px] p-6 border border-light-input-border dark:border-dark-input-border space-y-4"
          data-testid="bidding-post-card-buyer"
        >
          <TitleText
            text={t('biddingPosts.buyerInfo')}
            className="!text-[18px] !leading-[100%] !font-bold !text-light-text-primary dark:!text-dark-text-primary"
            data-testid="card-title-buyer"
          />

          {isOwnerLoading ? (
            <Skeleton active avatar paragraph={{ rows: 2 }} />
          ) : owner ? (
            <div className="space-y-4" data-testid="owner-info-content">
              {/* Row 1: Avatar on left, Name on right */}
              <div className="flex items-center gap-3.5" data-testid="buyer-profile-group">
                <div
                  className="w-[52px] h-[52px] rounded-full overflow-hidden shrink-0 flex items-center justify-center bg-white dark:bg-dark-surface-primary border border-light-input-border dark:border-dark-input-border"
                  data-testid="buyer-avatar-container"
                >
                  {owner.image && !avatarError ? (
                    <div className="w-full h-full rounded-full overflow-hidden [&_.ant-image]:!w-full [&_.ant-image]:!h-full [&_.ant-image-img]:!w-full [&_.ant-image-img]:!h-full [&_.ant-image-img]:!object-cover">
                      <Image
                        src={owner.image}
                        alt={owner.name ?? ''}
                        className="!w-full !h-full !object-cover"
                        preview={false}
                        onError={() => setAvatarError(true)}
                      />
                    </div>
                  ) : (
                    <div className="w-full h-full rounded-full bg-soft-background dark:bg-dark-surface-secondary flex items-center justify-center">
                      <InfoText
                        text={(owner.name || 'U').charAt(0).toUpperCase()}
                        className="!text-lg !font-bold text-light-text-primary dark:text-dark-text-primary"
                      />
                    </div>
                  )}
                </div>

                <div className="min-w-0 flex-1" data-testid="buyer-name-wrapper">
                  <InfoText
                    text={owner.name ?? '-'}
                    className="!text-[16px] sm:!text-[18px] !leading-[130%] !font-bold !text-light-text-primary dark:!text-dark-text-primary break-words"
                    data-testid="buyer-name"
                  />
                </div>
              </div>

              {/* Row 2: Stats (Rating + Orders) */}
              <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5" data-testid="buyer-stats-container">
                {ratingDisplay && (
                  <div className="flex items-center gap-1.5" data-testid="buyer-rating-wrapper">
                    <MagicStar size={16} variant="Bold" className="text-light-alert dark:text-dark-alert shrink-0" />
                    <InfoText
                      text={ratingDisplay}
                      className="!font-normal !text-[14px] !leading-[100%] text-light-text-primary dark:text-dark-text-primary"
                      data-testid="buyer-rating-text"
                    />
                  </div>
                )}
                {ordersDisplay && (
                  <div className="flex items-center gap-1.5" data-testid="buyer-orders-wrapper">
                    <ShoppingBag size={16} variant="Bold" className="text-light-information dark:text-dark-information shrink-0" />
                    <InfoText
                      text={ordersDisplay}
                      className="!font-normal !text-[14px] !leading-[100%] text-light-text-primary dark:text-dark-text-primary"
                      data-testid="buyer-orders-text"
                    />
                  </div>
                )}
              </div>

              {/* Row 3: Avg Response */}
              {avgResponseDisplay && (
                <div className="flex items-center gap-1.5 text-light-success dark:text-dark-success" data-testid="buyer-response-time">
                  <Timer size={16} variant="Bold" className="text-light-success dark:text-dark-success shrink-0" />
                  <InfoText
                    text={avgResponseDisplay}
                    className="!font-normal !text-[14px] !leading-[100%] text-light-text-primary dark:text-dark-text-primary"
                    data-testid="buyer-response-time-text"
                  />
                </div>
              )}

              {/* Row 4: Track KPIs Button */}
              <Button
                variant="outline"
                className="w-full !rounded-[12px] !h-[44px] !border-light-input-border dark:!border-dark-input-border justify-center !font-semibold !text-sm !text-light-text-primary dark:!text-dark-text-primary mt-1"
                data-testid="buyer-track-kpis-btn"
              >
                {t('biddingPosts.trackKPIs')}
              </Button>
            </div>
          ) : (
            <InfoText text="-" className="!text-sm text-light-text-secondary dark:text-dark-text-secondary" />
          )}
        </div>

        {/* 2. Basic Information */}
        <div
          className="bg-white dark:bg-dark-surface-primary rounded-[16px] p-6 border border-light-input-border dark:border-dark-input-border space-y-5"
          data-testid="bidding-post-card-basic-info"
        >
          <TitleText
            text={t('biddingPosts.basicInformation')}
            className="!text-lg !font-bold text-light-text-primary dark:text-dark-text-primary"
            data-testid="card-title-basic-info"
          />

          {isInfoLoading ? (
            <Skeleton active paragraph={{ rows: 4 }} />
          ) : info ? (
            <div className="space-y-4" data-testid="basic-info-content">
              <div>
                <InfoText
                  text={t('biddingPosts.suppliesTitle')}
                  className="!text-sm !font-normal text-text-secondary dark:text-dark-text-placeholder mb-1"
                />
                <InfoText
                  text={info.productName ?? '-'}
                  className="!text-base !font-bold text-light-text-primary dark:text-dark-text-primary"
                />
              </div>

              <div>
                <InfoText
                  text={t('biddingPosts.productQuantity')}
                  className="!text-sm !font-normal text-text-secondary dark:text-dark-text-placeholder mb-1"
                />
                <InfoText
                  text={info.quantity !== undefined ? `${info.quantity} ${quantityUnit}` : '-'}
                  className="!text-base !font-bold text-light-text-primary dark:text-dark-text-primary"
                />
              </div>

              {info.industry && (
                <div>
                  <div className="flex items-center gap-3">
                    <div
                      className="w-12 h-12 rounded-[12px] bg-blue-50 dark:bg-sky-950/40 text-light-information dark:text-dark-information flex items-center justify-center flex-shrink-0"
                      data-testid="industry-icon-container"
                    >
                      <Element4 size={24} variant="Bold" color="currentColor" />
                    </div>
                    <div>
                      <InfoText
                        text={t('biddingPosts.industry')}
                        className="!text-sm !font-normal text-text-secondary dark:text-dark-text-placeholder mb-0.5"
                      />
                      <InfoText
                        text={info.industry.name}
                        className="!text-base !font-bold text-light-text-primary dark:text-dark-text-primary"
                      />
                    </div>
                  </div>
                </div>
              )}

              {info.specialties && info.specialties.length > 0 && (
                <div>
                  <InfoText
                    text={t('biddingPosts.specialties')}
                    className="!text-sm !font-normal text-text-secondary dark:text-dark-text-placeholder mb-2"
                  />
                  <div className="flex flex-wrap gap-2" data-testid="specialties-list">
                    {info.specialties.map((spec, i) => (
                      <div
                        key={spec.id || `spec-${i}`}
                        className="px-3 py-2 rounded-[8px] bg-white dark:bg-dark-surface-primary border border-light-input-border dark:border-dark-input-border shadow-[0_1px_2px_rgba(0,0,0,0.04)] inline-flex items-center max-w-full"
                        data-testid={`specialty-item-${spec.id || i}`}
                      >
                        <InfoText
                          text={spec.name}
                          className="!text-sm !font-normal text-light-text-primary dark:text-dark-text-primary !leading-none break-words"
                        />
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ) : (
            <InfoText text="-" className="!text-sm text-light-text-secondary dark:text-dark-text-secondary" />
          )}
        </div>

        {/* 3. Location */}
        <div
          className="bg-white dark:bg-dark-surface-primary rounded-[16px] p-6 border border-light-input-border dark:border-dark-input-border space-y-4"
          data-testid="bidding-post-card-location"
        >
          <TitleText
            text={t('biddingPosts.location')}
            className="!text-lg !font-bold text-light-text-primary dark:text-dark-text-primary"
            data-testid="card-title-location"
          />

          {isLocationsLoading ? (
            <Skeleton active paragraph={{ rows: 2 }} />
          ) : primaryLocation ? (
            <>
              <div>
                <InfoText
                  text={t('biddingPosts.receivingLocation')}
                  className="!text-sm !font-normal text-text-secondary dark:text-dark-text-placeholder mb-1"
                />
                <InfoText
                  text={primaryLocation.address ?? '-'}
                  className="!text-base !font-bold text-light-text-primary dark:text-dark-text-primary"
                />
              </div>

              <div
                className="w-full h-[254px] rounded-[8px] border border-light-input-border dark:border-dark-input-border overflow-hidden relative [&_.leaflet-container]:grayscale"
                data-testid="location-map-container"
              >
                <Map
                  location={{
                    lat: hasValidCoords ? numLat : undefined,
                    lng: hasValidCoords ? numLng : undefined,
                    currentAddress: primaryLocation.address,
                  }}
                  viewOnly
                  height="100%"
                  width="100%"
                  dataTestId="bidding-post-location-map"
                />
              </div>
            </>
          ) : (
            <InfoText text="-" className="!text-sm text-light-text-secondary dark:text-dark-text-secondary" />
          )}
        </div>
      </div>

      {/* ── Right Column (~67% / 8 cols) ────────────────────────────────── */}
      <div className="lg:col-span-8 space-y-6" data-testid="bidding-post-info-right-column">
        {/* 1. Images & Videos */}
        <div
          className="bg-white dark:bg-dark-surface-primary rounded-[16px] p-6 border border-light-input-border dark:border-dark-input-border space-y-4"
          data-testid="bidding-post-section-images"
        >
          <TitleText
            text={t('biddingPosts.imagesAndVideos')}
            className="!text-lg !font-bold text-light-text-primary dark:text-dark-text-primary"
            data-testid="section-title-images"
          />
          {isAttachmentsLoading ? (
            <Skeleton active paragraph={{ rows: 2 }} />
          ) : media.length > 0 ? (
            <div className="flex items-center gap-4 overflow-x-auto pb-1" data-testid="images-container">
              {media.map((item, idx) => (
                <div
                  key={`${item.id}-${idx}`}
                  className="w-28 h-28 rounded-2xl border border-light-input-border dark:border-dark-input-border overflow-hidden bg-gray-50 dark:bg-gray-800 flex-shrink-0 [&_.ant-image]:!w-full [&_.ant-image]:!h-full [&_.ant-image-img]:!w-full [&_.ant-image-img]:!h-full [&_.ant-image-img]:!object-cover"
                  data-testid={`image-item-${item.id || idx}`}
                >
                  {item.mediaType === 'image' ? (
                    <div
                      className="w-full h-full cursor-pointer"
                      onClick={() =>
                        setActivePreviewDoc({
                          url: item.blobFileUrl,
                          name: item.name || `Product Image ${idx + 1}`,
                          isImage: true,
                        })
                      }
                      data-testid={`image-preview-trigger-${item.id || idx}`}
                    >
                      <Image
                        src={item.blobFileUrl}
                        alt={item.name || `Product Image ${idx + 1}`}
                        className="!w-full !h-full !object-cover pointer-events-none"
                        preview={false}
                      />
                    </div>
                  ) : (
                    <div
                      className="relative w-full h-full cursor-pointer group bg-black"
                      onClick={() => setActiveVideo({ url: item.blobFileUrl, name: item.name })}
                      data-testid={`video-preview-trigger-${item.id || idx}`}
                    >
                      <video
                        src={item.blobFileUrl}
                        preload="metadata"
                        className="w-full h-full object-cover pointer-events-none"
                      />
                      <div
                        className="absolute inset-0 bg-black/30 group-hover:bg-black/40 transition-colors flex items-center justify-center"
                        data-testid={`video-overlay-${item.id || idx}`}
                      >
                        <PlayCircle
                          size="32"
                          variant="Bold"
                          className="text-white group-hover:scale-110 transition-transform"
                        />
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <InfoText
              text={t('biddingPosts.noMediaUploaded')}
              className="!text-sm text-light-text-secondary dark:text-dark-text-secondary"
            />
          )}
        </div>

        {/* 2. Product Specs */}
        {info && info.specs && info.specs.length > 0 && (
          <div
            className="bg-white dark:bg-dark-surface-primary rounded-[16px] p-6 border border-light-input-border dark:border-dark-input-border space-y-4"
            data-testid="bidding-post-section-specs"
          >
            <TitleText
              text={t('biddingPosts.productSpecs')}
              className="!text-lg !font-bold text-light-text-primary dark:text-dark-text-primary"
              data-testid="section-title-specs"
            />
            <div className="overflow-x-auto" data-testid="specs-table-wrapper">
              <table className="w-full text-start" data-testid="specs-table">
                <thead>
                  <tr className="border-b border-light-input-border dark:border-dark-input-border">
                    <th className="py-3 px-3 text-start">
                      <InfoText
                        text={t('biddingPosts.specType')}
                        className="!text-sm !font-normal text-light-text-primary dark:text-dark-text-primary !leading-none text-start"
                      />
                    </th>
                    <th className="py-3 px-3 text-end">
                      <InfoText
                        text={t('biddingPosts.specValue')}
                        className="!text-base !font-bold text-light-text-primary dark:text-dark-text-primary !leading-none text-end"
                      />
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-light-input-border dark:divide-dark-input-border">
                  {info.specs.map((spec, index) => (
                    <tr
                      key={`spec-${index}`}
                      className={index % 2 === 0 ? 'bg-soft-background dark:bg-gray-800/30' : ''}
                    >
                      <td className="py-3 px-3 text-start">
                        <InfoText
                          text={spec.type}
                          className="!text-sm !font-normal text-light-text-primary dark:text-dark-text-primary text-start"
                        />
                      </td>
                      <td className="py-3 px-3 text-end">
                        <InfoText
                          text={spec.value}
                          className="!text-base !font-bold text-light-text-primary dark:text-dark-text-primary text-end"
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* 3. Uploaded Documents (only display if documents exist) */}
        {(isAttachmentsLoading || otherDocs.length > 0) && (
          <div
            className="bg-white dark:bg-dark-surface-primary rounded-[16px] p-6 border border-light-input-border dark:border-dark-input-border space-y-4"
            data-testid="bidding-post-section-documents"
          >
            <TitleText
              text={t('biddingPosts.uploadedDocuments')}
              className="!text-lg !font-bold text-light-text-primary dark:text-dark-text-primary"
              data-testid="section-title-documents"
            />
            {isAttachmentsLoading ? (
              <Skeleton active paragraph={{ rows: 2 }} />
            ) : (
              <div className="divide-y divide-gray-100 dark:divide-gray-800/80" data-testid="uploaded-documents-list">
                {otherDocs.map((doc, idx) => (
                  <div
                    key={doc.id}
                    className={`flex items-center justify-between ${idx === 0 ? 'pb-4' : 'py-4'}`}
                    data-testid={`document-item-${doc.id}`}
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-6 h-6 flex items-center justify-center flex-shrink-0 text-light-text-primary dark:text-dark-text-primary">
                        <Gallery size={24} variant="Bold" />
                      </div>
                      <div>
                        <InfoText
                          text={doc.name}
                          className="!text-sm !font-bold text-light-text-primary dark:text-dark-text-primary"
                        />
                        <InfoText
                          text={formatFileSize(doc.blobSize)}
                          className="!text-xs text-light-text-secondary dark:text-dark-text-secondary"
                        />
                      </div>
                    </div>
                    <div className="flex items-center gap-2.5">
                      <button
                        type="button"
                        onClick={() => handleDownloadDoc(doc.blobFileUrl, doc.name)}
                        className="w-8 h-8 rounded-full bg-green-50 dark:bg-emerald-950/50 text-light-success dark:text-emerald-400 flex items-center justify-center hover:opacity-80 transition-opacity cursor-pointer"
                        data-testid={`doc-download-${doc.id}`}
                      >
                        <DocumentDownload size={16} />
                      </button>
                      <button
                        type="button"
                        onClick={() => handleViewDoc(doc.blobFileUrl, doc.name)}
                        className="w-8 h-8 rounded-full bg-blue-50 dark:bg-sky-950/50 text-light-information dark:text-sky-400 flex items-center justify-center hover:opacity-80 transition-opacity cursor-pointer"
                        data-testid={`doc-view-${doc.id}`}
                      >
                        <Eye size={16} />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* 4. Evaluation Requirements */}
        <div
          className="bg-white dark:bg-dark-surface-primary rounded-[16px] p-6 border border-light-input-border dark:border-dark-input-border space-y-4"
          data-testid="bidding-post-section-evaluation"
        >
          <TitleText
            text={t('biddingPosts.evaluationRequirements')}
            className="!text-lg !font-bold text-light-text-primary dark:text-dark-text-primary"
            data-testid="section-title-evaluation"
          />
          <div className="space-y-4" data-testid="evaluation-content">
            <div>
              <InfoText
                text={t('biddingPosts.performEvaluation')}
                className="!text-sm !font-normal text-text-secondary dark:text-dark-text-placeholder mb-2"
              />
              <div
                className={`inline-flex items-center gap-[8px] px-[12px] py-[8px] h-[36px] rounded-[8px] ${performEvaluation
                    ? 'bg-green-50 dark:bg-emerald-950/40 text-light-success dark:text-dark-success'
                    : 'bg-soft-background dark:bg-dark-surface-secondary text-light-text-secondary dark:text-dark-text-secondary border border-light-input-border dark:border-dark-input-border'
                  }`}
                data-testid="evaluation-status-badge"
              >
                {performEvaluation && (
                  <Verify size={18} variant="Bold" className="text-light-success dark:text-dark-success" />
                )}
                <InfoText
                  text={performEvaluation ? t('biddingPosts.yes') : t('biddingPosts.no')}
                  className={`!text-sm !font-semibold ${performEvaluation
                      ? 'text-light-success dark:text-dark-success'
                      : 'text-light-text-secondary dark:text-dark-text-secondary'
                    }`}
                />
              </div>
            </div>

            {performEvaluation && (
              <div>
                <InfoText
                  text={t('biddingPosts.evaluationCriteriaDocument')}
                  className="!text-xs !font-normal text-light-text-secondary dark:text-dark-text-secondary mb-2"
                />
                <div className="divide-y divide-gray-100 dark:divide-gray-800/80">
                  {evalDocs.map((doc, idx) => (
                    <div
                      key={doc.id}
                      className={`flex items-center justify-between ${idx === 0 ? 'pb-4' : 'py-4'}`}
                      data-testid={`eval-doc-${doc.id}`}
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-6 h-6 flex items-center justify-center flex-shrink-0 text-light-text-primary dark:text-dark-text-primary">
                          <Gallery size={24} variant="Bold" />
                        </div>
                        <div>
                          <InfoText
                            text={doc.name}
                            className="!text-sm !font-bold text-light-text-primary dark:text-dark-text-primary"
                          />
                          <InfoText
                            text={formatFileSize(doc.blobSize)}
                            className="!text-xs text-light-text-secondary dark:text-dark-text-secondary"
                          />
                        </div>
                      </div>
                      <div className="flex items-center gap-2.5">
                        <button
                          type="button"
                          onClick={() => handleDownloadDoc(doc.blobFileUrl, doc.name)}
                          className="w-8 h-8 rounded-full bg-green-50 dark:bg-emerald-950/50 text-light-success dark:text-emerald-400 flex items-center justify-center hover:opacity-80 transition-opacity cursor-pointer"
                          data-testid={`eval-doc-download-${doc.id}`}
                        >
                          <DocumentDownload size={16} />
                        </button>
                        <button
                          type="button"
                          onClick={() => handleViewDoc(doc.blobFileUrl, doc.name)}
                          className="w-8 h-8 rounded-full bg-blue-50 dark:bg-sky-950/50 text-light-information dark:text-sky-400 flex items-center justify-center hover:opacity-80 transition-opacity cursor-pointer"
                          data-testid={`eval-doc-view-${doc.id}`}
                        >
                          <Eye size={16} />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* 5. Budget Method & Timeline */}
        {payment && (
          <div
            className="bg-white dark:bg-dark-surface-primary rounded-[16px] p-6 border border-light-input-border dark:border-dark-input-border space-y-4"
            data-testid="bidding-post-section-budget"
          >
            <TitleText
              text={t('biddingPosts.budgetMethodAndTimeline')}
              className="!text-lg !font-bold text-light-text-primary dark:text-dark-text-primary"
              data-testid="section-title-budget"
            />
            {isPaymentLoading ? (
              <Skeleton active paragraph={{ rows: 2 }} />
            ) : (
              <div className="space-y-4" data-testid="budget-method-content">
                {/* Only render summary row if not milestones (or full budget), to match Figma design */}
                {!isMilestones && (
                  <div className="flex flex-wrap items-center gap-8">
                    <div>
                      <InfoText
                        text={t('biddingPosts.budgetMethod')}
                        className="!text-xs text-light-text-secondary dark:text-dark-text-secondary mb-1"
                      />
                      <InfoText
                        text={
                          payment.paymentMethod?.name ||
                          (payment.paymentMethod?.code === 'FULL_BUDGET'
                            ? t('biddingPosts.fullBudget')
                            : payment.paymentMethod?.code || '-')
                        }
                        className="!text-sm !font-bold text-light-text-primary dark:text-dark-text-primary"
                      />
                    </div>

                    {payment.totalQuantity !== undefined && (
                      <div>
                        <InfoText
                          text={t('biddingPosts.quantity')}
                          className="!text-xs text-light-text-secondary dark:text-dark-text-secondary mb-1"
                        />
                        <InfoText
                          text={`${payment.totalQuantity} ${payment.quantityUnit ?? ''}`}
                          className="!text-sm !font-bold text-light-text-primary dark:text-dark-text-primary"
                        />
                      </div>
                    )}

                    {(payment.totalDeliveryTime !== undefined || payment.expectedDeliveryTimeDays !== undefined) && (
                      <div>
                        <InfoText
                          text={t('biddingPosts.expectedDeliveryTime')}
                          className="!text-xs text-light-text-secondary dark:text-dark-text-secondary mb-1"
                        />
                        <InfoText
                          text={t('components.postDetails.deliveryDays', {
                            count: Number(payment.totalDeliveryTime ?? payment.expectedDeliveryTimeDays),
                          })}
                          className="!text-sm !font-bold text-light-text-primary dark:text-dark-text-primary"
                        />
                      </div>
                    )}
                  </div>
                )}

                {/* If Milestones exist */}
                {isMilestones && payment.milestones && payment.milestones.length > 0 && (
                  <div className="space-y-3">
                    <div>
                      <InfoText
                        text={t('biddingPosts.numberOfMilestones')}
                        className="!text-sm text-text-secondary dark:text-dark-text-secondary mb-1"
                      />
                      <InfoText
                        text={t('biddingPosts.milestonesCountText', {
                          count: payment.milestoneCount ?? payment.milestones.length,
                        })}
                        className="!text-lg !font-bold text-light-text-primary dark:text-dark-text-primary"
                      />
                    </div>

                    <div className="overflow-x-auto pt-1">
                      <table className="w-full text-start" data-testid="milestones-table">
                        <thead>
                          <tr className="border-b border-light-input-border dark:border-dark-input-border">
                            <th className="py-3 px-3 text-start !text-light-text-primary dark:!text-dark-text-primary">
                              <InfoText
                                text={t('biddingPosts.deliveryDate')}
                                className="!text-sm !font-normal !text-light-text-primary dark:!text-dark-text-primary !leading-none text-start"
                              />
                            </th>
                            <th className="py-3 px-3 text-end !text-light-text-primary dark:!text-dark-text-primary">
                              <InfoText
                                text={t('biddingPosts.quantity')}
                                className="!text-sm !font-bold !text-light-text-primary dark:!text-dark-text-primary !leading-none text-end"
                              />
                            </th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-light-input-border dark:divide-dark-input-border">
                          {payment.milestones.map((ms, idx) => (
                            <tr
                              key={`ms-${idx}`}
                              className={idx % 2 === 0 ? 'bg-soft-background dark:bg-gray-800/30' : ''}
                            >
                              <td className="py-3 px-3 text-start">
                                <InfoText
                                  text={
                                    ms.deliveryTime !== undefined
                                      ? t('components.postDetails.deliveryDays', { count: Number(ms.deliveryTime) })
                                      : '-'
                                  }
                                  className="!text-sm !font-normal text-light-text-primary dark:text-dark-text-primary text-start"
                                />
                              </td>
                              <td className="py-3 px-3 text-end">
                                <InfoText
                                  text={ms.quantity !== undefined ? `${ms.quantity}` : '-'}
                                  className="!text-base !font-bold text-light-text-primary dark:text-dark-text-primary text-end"
                                />
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      <CustomModal
        centered
        open={!!activeVideo}
        onCancel={() => setActiveVideo(null)}
        title={activeVideo?.name || t('components.preview_file')}
        data-testid="bidding-post-video-preview-modal"
      >
        <div className="space-y-4" data-testid="bidding-post-video-preview-content">
          <div className="h-[400px] w-full flex items-center justify-center bg-black rounded-lg overflow-hidden" data-testid="bidding-post-video-preview-container">
            {activeVideo && (
              <video
                src={activeVideo.url}
                controls
                autoPlay
                className="w-full h-full object-contain rounded-lg"
                data-testid="bidding-post-preview-video-player"
              />
            )}
          </div>
          <div className="flex justify-end pt-2" data-testid="bidding-post-video-preview-actions">
            <Button
              type="button"
              onClick={() => setActiveVideo(null)}
              className="!px-8 !py-2 !h-auto !rounded-lg"
              data-testid="bidding-post-video-preview-close-btn"
            >
              {t('done')}
            </Button>
          </div>
        </div>
      </CustomModal>

      <CustomModal
        centered
        open={!!activePreviewDoc}
        onCancel={() => setActivePreviewDoc(null)}
        title={activePreviewDoc?.name || t('components.preview_file')}
        data-testid="bidding-post-doc-preview-modal"
      >
        <div className="space-y-4" data-testid="bidding-post-doc-preview-content">
          <div className="h-[500px] w-full flex items-center justify-center bg-gray-50 dark:bg-gray-900 rounded-lg overflow-hidden [&_.ant-image]:!h-full [&_.ant-image]:!w-full [&_.ant-image]:!flex [&_.ant-image]:!items-center [&_.ant-image]:!justify-center" data-testid="bidding-post-doc-preview-container">
            {activePreviewDoc && (
              activePreviewDoc.isImage ? (
                <Image
                  src={activePreviewDoc.url}
                  alt={activePreviewDoc.name || 'Document'}
                  className="!max-h-[500px] !w-full !object-contain"
                  preview={false}
                  data-testid="bidding-post-doc-preview-image"
                />
              ) : (
                <iframe
                  src={`https://docs.google.com/viewer?url=${encodeURIComponent(activePreviewDoc.url)}&embedded=true`}
                  className="w-full h-full border-0"
                  title={activePreviewDoc.name || 'Document Viewer'}
                  data-testid="bidding-post-doc-preview-iframe"
                />
              )
            )}
          </div>
          <div className="flex justify-end pt-2" data-testid="bidding-post-doc-preview-actions">
            <Button
              type="button"
              onClick={() => setActivePreviewDoc(null)}
              className="!px-8 !py-2 !h-auto !rounded-lg"
              data-testid="bidding-post-doc-preview-close-btn"
            >
              {t('done')}
            </Button>
          </div>
        </div>
      </CustomModal>
    </div>
  );
}
